class NotifyMePopup {
  static POPUP_ID = 'NotifyMePopup';
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static BODY_OPEN_CLASS = 'notify-me-popup-open';

  constructor(element) {
    this.el = element;
    this.form = this.el.querySelector('[data-notify-form]');
    this.submitBtn = this.el.querySelector('[data-notify-submit]');
    this.titleEl = this.el.querySelector('[data-notify-product-title]');
    this.variantEl = this.el.querySelector('[data-notify-product-variant]');
    this.inputs = {
      productId: this.el.querySelector('[data-notify-product-id-input]'),
      variantId: this.el.querySelector('[data-notify-variant-id-input]'),
      productHandle: this.el.querySelector('[data-notify-product-handle-input]'),
      productUrl: this.el.querySelector('[data-notify-product-url-input]'),
      name: this.el.querySelector('[data-notify-name]'),
      email: this.el.querySelector('[data-notify-email]'),
    };

    if (this.submitBtn && !this.submitBtn.dataset.defaultLabel) {
      this.submitBtn.dataset.defaultLabel = this.submitBtn.textContent.trim();
    }

    this._bindEvents();
  }

  _bindEvents() {
    document.addEventListener('click', this._handleDocumentClick.bind(this));
    document.addEventListener('keydown', this._handleKeydown.bind(this));
    if (this.form) {
      this.form.addEventListener('submit', this._handleSubmit.bind(this));
    }
  }

  _handleDocumentClick(e) {
    const trigger = e.target.closest('[data-notify-trigger]');
    if (trigger) {
      e.preventDefault();
      this.open(trigger);
      return;
    }

    const closeBtn = e.target.closest('[data-notify-close]');
    if (closeBtn) {
      e.preventDefault();
      this.close();
      return;
    }

    if (this.el.dataset.open === 'true' && e.target === this.el) {
      this.close();
    }
  }

  _handleKeydown(e) {
    if (e.key !== 'Escape') return;
    if (this.el.dataset.open === 'true') this.close();
  }

  open(trigger) {
    const data = {
      productTitle: trigger.dataset.productTitle || '',
      productId: trigger.dataset.productId || '',
      productHandle: trigger.dataset.productHandle || '',
      productUrl: trigger.dataset.productUrl || '',
      variantId: trigger.dataset.variantId || '',
      variantTitle: trigger.dataset.variantTitle || '',
    };

    this._populate(data);
    this.reset();

    this.el.dataset.open = 'true';
    this.el.dataset.state = 'idle';
    document.body.classList.add(NotifyMePopup.BODY_OPEN_CLASS);

    if (this.inputs.email) {
      setTimeout(() => this.inputs.email.focus(), 50);
    }
  }

  close() {
    this.el.dataset.open = 'false';
    document.body.classList.remove(NotifyMePopup.BODY_OPEN_CLASS);
  }

  _populate(data) {
    if (this.titleEl) this.titleEl.textContent = data.productTitle;
    if (this.variantEl) {
      const hasVariant =
        data.variantTitle && data.variantTitle !== 'Default Title';
      this.variantEl.textContent = hasVariant ? data.variantTitle : '';
      this.variantEl.hidden = !hasVariant;
    }
    if (this.inputs.productId) this.inputs.productId.value = data.productId;
    if (this.inputs.variantId) this.inputs.variantId.value = data.variantId;
    if (this.inputs.productHandle) this.inputs.productHandle.value = data.productHandle;
    if (this.inputs.productUrl) this.inputs.productUrl.value = data.productUrl;
  }

  reset() {
    if (this.form) this.form.reset();
    this._clearErrors();
    this.el.dataset.state = 'idle';
    if (this.submitBtn) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent =
        this.submitBtn.dataset.defaultLabel || 'Notify Me';
    }
  }

  _clearErrors() {
    this.el.querySelectorAll('[data-notify-error]').forEach((el) => {
      el.textContent = '';
    });
    this.el.querySelectorAll('.notify-me-popup__input').forEach((el) => {
      el.setAttribute('aria-invalid', 'false');
    });
    const formError = this.el.querySelector('[data-notify-form-error]');
    if (formError) formError.textContent = '';
  }

  _setError(fieldName, message) {
    const errorEl = this.el.querySelector(`[data-notify-error="${fieldName}"]`);
    const input = this.el.querySelector(`[data-notify-${fieldName}]`);
    if (errorEl) errorEl.textContent = message;
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  _validate(values) {
    this._clearErrors();
    let valid = true;

    if (!values.name || values.name.trim().length < 2) {
      this._setError('name', 'Please enter your name');
      valid = false;
    }
    if (!values.email || !NotifyMePopup.EMAIL_REGEX.test(values.email)) {
      this._setError('email', 'Please enter a valid email address');
      valid = false;
    }

    return valid;
  }

  async _handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const values = {
      name: (formData.get('contact[name]') || '').toString(),
      email: (formData.get('contact[email]') || '').toString(),
      productId: (formData.get('product_id') || '').toString(),
      variantId: (formData.get('variant_id') || '').toString(),
      productHandle: (formData.get('product_handle') || '').toString(),
      productUrl: (formData.get('product_url') || '').toString(),
    };

    if (!this._validate(values)) return;

    this._setSubmitting(true);

    try {
      await this._submitNotification(values);
      this.el.dataset.state = 'success';
      window.dispatchEvent(
        new CustomEvent('notify-me:submitted', { detail: values })
      );
    } catch (error) {
      console.error('[Notify Me]', error);
      this._setFormError(
        error.message || 'Unable to send right now. Please try again later.'
      );
      this._setSubmitting(false);
    }
  }

  _setFormError(message) {
    let formError = this.el.querySelector('[data-notify-form-error]');
    if (!formError) {
      formError = document.createElement('p');
      formError.className = 'notify-me-popup__form-error';
      formError.setAttribute('data-notify-form-error', '');
      formError.setAttribute('role', 'alert');
      if (this.submitBtn && this.submitBtn.parentNode) {
        this.submitBtn.parentNode.insertBefore(formError, this.submitBtn);
      } else if (this.form) {
        this.form.appendChild(formError);
      }
    }
    formError.textContent = message;
  }

  _setSubmitting(isSubmitting) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isSubmitting;
    this.submitBtn.textContent = isSubmitting
      ? 'Sending…'
      : this.submitBtn.dataset.defaultLabel || 'Notify Me';
  }

  async _submitNotification(values) {
    const endpoint =
      (this.el && this.el.dataset.notifyEndpoint) ||
      (this.form && this.form.dataset.notifyEndpoint);

    if (!endpoint) {
      throw new Error(
        'No endpoint configured. Set the Google Sheet webhook URL in theme settings.'
      );
    }

    const payload = new URLSearchParams();
    payload.append('name', values.name);
    payload.append('email', values.email);
    payload.append('product_id', values.productId);
    payload.append('variant_id', values.variantId);
    payload.append('product_handle', values.productHandle);
    payload.append('product_url', values.productUrl);
    payload.append('submitted_at', new Date().toISOString());

    let response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: payload.toString(),
      });
    } catch (networkError) {
      throw new Error('Network error. Please check your connection and try again.');
    }

    if (!response.ok) {
      throw new Error(`Server returned status ${response.status}`);
    }

    let data;
    try {
      data = await response.json();
    } catch (_) {
      throw new Error('Unexpected server response. Please try again.');
    }

    if (data && data.ok === false) {
      throw new Error(data.error || 'Submission was rejected. Please try again.');
    }

    return data || { ok: true };
  }

  static init() {
    const el = document.getElementById(NotifyMePopup.POPUP_ID);
    if (!el || el.dataset.notifyInitialised === 'true') return null;
    el.dataset.notifyInitialised = 'true';
    return new NotifyMePopup(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => NotifyMePopup.init());
} else {
  NotifyMePopup.init();
}
